import { useState } from 'react'
import { useStore as useZustandStore } from 'zustand'
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu'
import {
  Building2,
  CheckCircle2,
  ChevronDown,
  CircleHelp,
  CloudOff,
  Copy,
  FileDown,
  FileUp,
  GitCompare,
  LayoutTemplate,
  LogIn,
  LogOut,
  Plus,
  Redo2,
  Sparkles,
  Undo2,
  UserRound,
} from 'lucide-react'
import { BRAND_LOGO } from '@/lib/brandAssets'
import useStore, { redoStore, undoStore, visiblePlanKeys } from '@/stores/useStore'
import useClientBook from '@/stores/useClientBook'
import PlanChips from '@/components/PlanChips'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

const ASSISTANT_LABELS = {
  people: 'Ask about employees',
  plan: 'Ask about plan design',
  checks: 'Ask about checks',
  results: 'Ask about results',
  reports: 'Ask about this report',
}

const utilityButtonClass = 'inline-flex h-8 shrink-0 items-center justify-center gap-1.5 rounded px-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-40'

export default function Header({
  hasEmployees,
  stage = 'people',
  onLogoClick,
  onContactClick,
  onPrivacyClick,
  onSavePlanAs,
  onManageClients,
  onNewClient,
  onOpenPlanTemplates,
  onImportPlanSettings,
  onDownloadPlanSettings,
  onRetrySave,
  loginHref = null,
  account = null,
  showWorkspaceActions = true,
}) {
  const openUtilityDialog = useStore((state) => state.openUtilityDialog)
  const clientBookEnabled = useClientBook((state) => state.enabled)
  const clients = useClientBook((state) => state.clients)
  const activeClientId = useClientBook((state) => state.activeClientId)
  const syncStatus = useClientBook((state) => state.syncStatus)
  const visiblePlanCount = useStore((state) => visiblePlanKeys(state).length)
  const comparing = visiblePlanCount > 1
  const [compareOpen, setCompareOpen] = useState(false)
  const [accountOpen, setAccountOpen] = useState(false)

  const canUndo = useZustandStore(useStore.temporal, (state) => state.pastStates.length > 0)
  const canRedo = useZustandStore(useStore.temporal, (state) => state.futureStates.length > 0)
  const assistantLabel = ASSISTANT_LABELS[stage] || 'Ask Julio'
  const activeClient = clients.find((client) => client.id === activeClientId) || null
  const clientLabel = activeClient?.client_name || (account ? 'Untitled client' : 'Current plan')
  const accountSyncAvailable = Boolean(account && clientBookEnabled)
  const saveState = !accountSyncAvailable
    ? { label: 'Saved on this device', icon: CheckCircle2, tone: 'text-muted-foreground' }
    : syncStatus === 'saving'
      ? { label: 'Saving…', icon: null, tone: 'text-muted-foreground' }
      : syncStatus === 'offline'
        ? { label: 'Offline · saved on this device', icon: CloudOff, tone: 'text-amber-700' }
        : syncStatus === 'error'
          ? { label: 'Couldn’t save · Retry', icon: CloudOff, tone: 'text-destructive' }
          : { label: 'Plan saved to account', icon: CheckCircle2, tone: 'text-muted-foreground' }

  const openAssistant = () => {
    window.dispatchEvent(new CustomEvent('julio:open', { detail: { context: stage } }))
  }

  const signIn = () => {
    if (loginHref) window.location.assign(loginHref)
  }

  const menuRowClass = 'flex h-9 w-full items-center gap-2 rounded px-2 text-left text-xs text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50'
  const dropdownItemClass = `${menuRowClass} cursor-default select-none data-[disabled]:pointer-events-none data-[highlighted]:bg-muted data-[disabled]:opacity-40`
  const SaveStateIcon = saveState.icon
  const saveStateShortLabel = syncStatus === 'saving'
    ? 'Saving'
    : syncStatus === 'offline'
      ? 'Device only'
      : syncStatus === 'error'
        ? 'Retry save'
        : 'Saved'
  const hasPlanMenu = Boolean(
    (account && (onSavePlanAs || onManageClients))
    || onOpenPlanTemplates
    || onImportPlanSettings
    || onDownloadPlanSettings,
  )

  return (
    <header className="sticky top-0 z-40 h-12 border-b border-border bg-card/95 px-3 backdrop-blur supports-[backdrop-filter]:bg-card/90 sm:px-5">
      <div className="mx-auto grid h-full max-w-[1600px] grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2">
        <button
          type="button"
          aria-label="Go to retira.app home"
          onClick={onLogoClick}
          className="inline-flex min-w-0 shrink items-center gap-2 rounded pr-2 text-left transition-colors hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
        >
          <picture className="shrink-0">
            <source type="image/avif" srcSet={BRAND_LOGO.avifSrcSet} sizes="24px" />
            <img
              src={BRAND_LOGO.png}
              srcSet={BRAND_LOGO.pngSrcSet}
              sizes="24px"
              width="24"
              height="24"
              alt=""
              className="h-6 w-6 rounded object-cover"
            />
          </picture>
          <span className="hidden whitespace-nowrap text-sm font-semibold tracking-tight min-[700px]:inline">retira.app</span>
        </button>

        {showWorkspaceActions ? (
          <>
          <div role="toolbar" aria-label="Calculator actions" className="flex min-w-0 items-center justify-self-center gap-0.5">
            {onNewClient || hasPlanMenu ? (
              <div className="flex h-8 shrink-0 items-center rounded border border-border bg-background">
                {onNewClient ? (
                  <button
                    type="button"
                    onClick={onNewClient}
                    className={`${utilityButtonClass} h-full rounded-none px-2.5 text-foreground ${hasPlanMenu ? 'border-r border-border' : ''}`}
                    aria-label="Start here"
                  >
                    <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                    <span className="hidden whitespace-nowrap sm:inline">Start here</span>
                  </button>
                ) : null}

                {hasPlanMenu ? (
                  <DropdownMenuPrimitive.Root>
                <DropdownMenuPrimitive.Trigger asChild>
                  <button
                    type="button"
                    className={`${utilityButtonClass} h-full max-w-56 rounded-none px-2.5 text-foreground`}
                    aria-label={`Plan: ${clientLabel}`}
                  >
                    <Building2 className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    <span className="hidden max-w-36 truncate whitespace-nowrap min-[560px]:inline">{clientLabel}</span>
                    <ChevronDown className="h-3 w-3" aria-hidden="true" />
                  </button>
                </DropdownMenuPrimitive.Trigger>
                <DropdownMenuPrimitive.Portal>
                  <DropdownMenuPrimitive.Content
                    align="start"
                    sideOffset={7}
                    collisionPadding={8}
                    aria-label="Plan"
                    className="z-50 min-w-64 rounded-md border border-border bg-popover p-1.5 text-popover-foreground shadow-md"
                  >
                    <DropdownMenuPrimitive.Label className="px-2 py-1.5">
                      <span className="block truncate text-xs font-semibold">{clientLabel}</span>
                      <span className="block text-[11px] font-normal text-muted-foreground">Current plan workspace</span>
                    </DropdownMenuPrimitive.Label>

                    {account && (onSavePlanAs || onManageClients) ? (
                      <>
                        <DropdownMenuPrimitive.Separator className="my-1 h-px bg-border" />
                        {onSavePlanAs ? (
                          <DropdownMenuPrimitive.Item onSelect={onSavePlanAs} className={dropdownItemClass}>
                            <Copy className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                            {activeClient ? 'Duplicate client…' : 'Name this client…'}
                          </DropdownMenuPrimitive.Item>
                        ) : null}
                        {onManageClients ? (
                          <DropdownMenuPrimitive.Item onSelect={onManageClients} className={dropdownItemClass}>
                            <Building2 className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />Manage clients…
                          </DropdownMenuPrimitive.Item>
                        ) : null}
                      </>
                    ) : null}

                    {onOpenPlanTemplates || onImportPlanSettings || onDownloadPlanSettings ? (
                      <>
                        <DropdownMenuPrimitive.Separator className="my-1 h-px bg-border" />
                        <DropdownMenuPrimitive.Label className="px-2 py-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                          Plan tools
                        </DropdownMenuPrimitive.Label>
                        {onOpenPlanTemplates ? (
                          <DropdownMenuPrimitive.Item onSelect={onOpenPlanTemplates} className={dropdownItemClass}>
                            <LayoutTemplate className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />Plan templates…
                          </DropdownMenuPrimitive.Item>
                        ) : null}
                        {onImportPlanSettings ? (
                          <DropdownMenuPrimitive.Item onSelect={onImportPlanSettings} className={dropdownItemClass}>
                            <FileUp className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />Import plan file…
                          </DropdownMenuPrimitive.Item>
                        ) : null}
                        {onDownloadPlanSettings ? (
                          <DropdownMenuPrimitive.Item onSelect={onDownloadPlanSettings} className={dropdownItemClass}>
                            <FileDown className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />Download plan backup
                          </DropdownMenuPrimitive.Item>
                        ) : null}
                      </>
                    ) : null}
                  </DropdownMenuPrimitive.Content>
                </DropdownMenuPrimitive.Portal>
                  </DropdownMenuPrimitive.Root>
                ) : null}
              </div>
            ) : (
              <span className="hidden max-w-40 items-center gap-1.5 truncate px-2 text-xs font-medium text-foreground sm:inline-flex">
                <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />Current plan
              </span>
            )}

            <button
              type="button"
              onClick={syncStatus === 'error' ? onRetrySave : undefined}
              disabled={syncStatus !== 'error'}
              aria-live="polite"
              aria-label={saveState.label}
              title={saveState.label}
              className={`hidden h-8 items-center gap-1.5 whitespace-nowrap px-1.5 text-[11px] min-[640px]:inline-flex ${saveState.tone} ${syncStatus === 'error' ? 'cursor-pointer underline underline-offset-2' : 'disabled:opacity-100'}`}
            >
              {syncStatus === 'saving' ? <span className="h-2 w-2 animate-pulse rounded-full bg-current" aria-hidden="true" /> : SaveStateIcon ? <SaveStateIcon className="h-3 w-3" aria-hidden="true" /> : null}
              <span className="hidden min-[940px]:inline">{saveStateShortLabel}</span>
            </button>

            <span aria-hidden="true" className="mx-1 hidden h-4 w-px bg-border min-[520px]:block" />
            <div className="hidden items-center min-[520px]:flex">
              <button type="button" aria-label="Undo" title="Undo" onClick={undoStore} disabled={!canUndo} className={`${utilityButtonClass} w-8 px-0`}>
                <Undo2 className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
              <button type="button" aria-label="Redo" title="Redo" onClick={redoStore} disabled={!canRedo} className={`${utilityButtonClass} w-8 px-0`}>
                <Redo2 className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </div>

            {hasEmployees ? (
              <Popover open={compareOpen} onOpenChange={setCompareOpen}>
              <PopoverTrigger asChild>
                  <button type="button" className={`${utilityButtonClass} text-foreground`} aria-label={`Compare options: ${visiblePlanCount} shown`}>
                    <GitCompare className="h-3.5 w-3.5" aria-hidden="true" />
                    <span className="hidden min-[760px]:inline">Compare</span>
                    <span className={`inline-flex min-w-5 items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] tabular-nums ${comparing ? 'bg-foreground text-background' : 'border border-border text-muted-foreground'}`}>
                      {visiblePlanCount}<span className="sr-only"> shown</span>
                    </span>
                  </button>
                </PopoverTrigger>
                <PopoverContent align="end" sideOffset={7} className="w-[30rem] max-w-[calc(100vw-2rem)] space-y-3 p-4">
                  <div className="space-y-1">
                    <p className="text-sm font-semibold">Compare options</p>
                    <p className="text-xs text-muted-foreground">Choose which options appear side by side in Plan Settings, Checks, Results, and Reports.</p>
                  </div>
                  <PlanChips manage />
                </PopoverContent>
              </Popover>
            ) : null}

            <button type="button" onClick={openAssistant} className={utilityButtonClass} aria-label={assistantLabel}>
              <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
              <span className="hidden whitespace-nowrap min-[1020px]:inline">Ask Julio</span>
            </button>

            <span aria-hidden="true" className="mx-1 hidden h-4 w-px bg-border min-[700px]:block" />

            <button type="button" aria-label="Open documentation" title="Documentation" onClick={() => openUtilityDialog('docs')} className={`${utilityButtonClass} w-8 px-0`}>
              <CircleHelp className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </div>

            <Popover open={accountOpen} onOpenChange={setAccountOpen}>
              <PopoverTrigger asChild>
                <button type="button" className={`${utilityButtonClass} max-w-52`} aria-label="Open account menu">
                  <UserRound className="h-3.5 w-3.5" aria-hidden="true" />
                  <span className="hidden max-w-36 truncate min-[1240px]:inline">{account?.email || (loginHref ? 'Log in' : 'Account')}</span>
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" sideOffset={7} className="w-56 gap-1 p-1.5">
                {account ? (
                  <div className="border-b border-border px-2 py-2">
                    <p className="truncate text-xs font-medium" title={account.email}>{account.email}</p>
                  </div>
                ) : null}
                <button type="button" onClick={() => { setAccountOpen(false); onContactClick() }} className={menuRowClass}>Contact</button>
                <button type="button" onClick={() => { setAccountOpen(false); onPrivacyClick() }} className={menuRowClass}>Privacy &amp; Terms</button>
                {account ? (
                  <button type="button" onClick={account.onSignOut} className={menuRowClass}>
                    <LogOut className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />Sign out
                  </button>
                ) : loginHref ? (
                  <button type="button" onClick={signIn} className={menuRowClass}>
                    <LogIn className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />Log in
                  </button>
                ) : null}
              </PopoverContent>
            </Popover>
          </>
        ) : (
          <div className="col-span-2 justify-self-end">
            {loginHref ? <Button type="button" variant="ghost" size="sm" onClick={signIn}>Log in</Button> : null}
          </div>
        )}
      </div>
    </header>
  )
}
